<template>
  <div :class="'container'">
    <div :class="$style.header">
      <h1 :class="[$style.title, 'h2']">{{ t('download.title') }}</h1>
      <div :class="$style.caption">
        {{ t('download.caption') }}
      </div>
    </div>

    <div
      :class="$style.downloads"
    >
      <transition-group name="fade">
        <DownloadsCardSkeleton
          v-show="status !== 'success'"
          v-for="i of 4"
        />
      </transition-group>
      <transition-group name="fade">
        <DownloadsCard
          v-show="status === 'success'"
          v-for="(item, index) of listOfDownloads"
          :key="index"
        >
          <template #icon>
            <component :is="Icons[item.icon]" />
          </template>

          <template #title>
            {{ t(`download.${item.title}.title`) }}
          </template>
          <template #version>
            {{ item.version }}
          </template>

          <template #caption>
            <div v-html="t(`download.${item.title}.caption`)"></div>
          </template>

          <template #actions>
            <UiButton
              :to="item.url"
              @click="tracking(item.title)"
              wide
            >
              <template #label>
                {{ t(`download.${item.title}.button`) }}
              </template>
            </UiButton>

            <UiButton
              v-if="item.secondaryUrl"
              :to="item.secondaryUrl"
              @click="tracking(item.title)"
              wide
            >
              <template #label>
                {{ t(`download.${item.title}.buttonSecondary`) }}
              </template>
            </UiButton>
          </template>
        </DownloadsCard>
      </transition-group>
    </div>
  </div>
</template>

<script setup lang="ts">
import { eventTracker, AnalyticsEventKey } from '~/utils/analyticServices';
interface DownloadItemParams {
  icon: string,
  title: string,
  version: string,
  url: string,
  secondaryUrl?: string,
  secondaryTitle?: string,
}

const { t } = useI18n();

useHead({
  title: t('meta.downloads.title'),
});

const { $mixpanel } = useNuxtApp();

const toastStore = useToastStore();

const authStore = useAuthStore();
const {
  user,
} = storeToRefs(authStore);

const isEmeil = computed(() => user.value?.email ? user.value.email : '');

const winIcon = resolveComponent('UiIconsWindows');
const macIcon = resolveComponent('UiIconsApple');
const androidIcon = resolveComponent('UiIconsAndroid');
const iosIcon = resolveComponent('UiIconsIos');

const Icons = computed(() => ({
  win: winIcon,
  mac: macIcon,
  android: androidIcon,
  ios: iosIcon,
}));

const staticData = computed<DownloadItemParams[]>(() => [
  {
    icon: 'android',
    title: 'Android',
    version: '1.0.0',
    url: 'https://play.google.com/store/apps/details?id=com.aqum.app',
  }, {
    icon: 'ios',
    title: 'iOS',
    version: '1.0.0',
    url: 'https://apps.apple.com/us/app/aqum/id6447129587',
  },
]);

const fetchWindows = async (): Promise<DownloadItemParams | undefined> => {
  try {
    const downloadsApi = useNuxtApp().$downloadsApi;
    const { result, data } = await downloadsApi.getDownloadOptApi('win');

    if (result) {
      return {
        icon: 'win',
        title: data.platform,
        version: data.version,
        url: data.downloadURL,
      };
    } else {
      throw new Error('Error @getDownloadOptApi: windows')
    }
  } catch (error) {
    console.error(error);
    toastStore.showDanger({
      text: t('errors.download.error'),
      duration: 3000,
    });

    return undefined;
  }
};

const fetchMac = async (): Promise<DownloadItemParams | undefined> => {
  try {
    const downloadsApi = useNuxtApp().$downloadsApi;

    const { result: macResult, data: macData } = await downloadsApi.getDownloadOptApi('mac');
    const { result: armResult, data: armData } = await downloadsApi.getDownloadOptApi('mac_arm');

    if (macResult && armResult) {
      return {
        icon: 'mac',
        title: macData.platform,
        version: armData.version,
        url: armData.downloadURL,
        secondaryUrl: macData.downloadURL,
        secondaryTitle: armData.platform
      };
    } else {
      throw new Error('Error @getDownloadOptApi: mac & mac_arm')
    }
  } catch (error) {
    console.error(error);
    toastStore.showDanger({
      text: 'Couldn`t get an up-to-date link to download the apple app',
      duration: 3000,
    });

    return undefined;
  }
};

const fetchDownloads = async (): Promise<DownloadItemParams[]> => {
  try {
    const result: DownloadItemParams[] = [];

    const windows = await fetchWindows();
    const mac = await fetchMac();

    if (windows) result.push(windows);
    if (mac) result.push(mac);

    // result.push(...staticData.value);

    return result;
  } catch (error) {
    console.error(error);
    toastStore.showDanger({
      text: 'Couldn’t get a list of downloads',
      duration: 3000,
    });

    return [];
  }
};

const tracking = (item) => {
  $mixpanel.track('browser_download', {
    email: isEmeil.value,
    OS: item,
    utmSource: localStorage.getItem('utmSource'),
    utmMedium: localStorage.getItem('utmMedium'),
    utmCampaign: localStorage.getItem('utmCampaign'),
    utmContent: localStorage.getItem('utmContent'),
    utmTerm: localStorage.getItem('utmTerm'),
    refId: localStorage.getItem('refId'),
  });
  fbq('track', 'Lead');
  fbq('track', 'StartTrial');
}

const listOfDownloads = ref<DownloadItemParams[]>();
const status = ref<'pending' | 'success' | 'error'>('pending');

onMounted(async () => {
  try {
    const result = await fetchDownloads();
    listOfDownloads.value = result;
    status.value = 'success';
  } catch (e) {
    status.value = 'error';
    console.error('Error loading downloads:', e);
  }
  eventTracker(AnalyticsEventKey.DownloadPageViewed);
});
</script>

<style lang="scss" module>
  .downloads {
    display: grid;
    grid-template-columns: 1fr 1fr;
    //grid-template-rows: 1fr 1fr;
    max-width: 870px;
    margin: auto;
    gap: 27px;
    padding-bottom: 32px;

    @media (max-width: 767px) {
      display: flex;
      flex-direction: column;
      gap: 22px;
    }
  }

  .header {
    padding: 32px 0;
    text-align: center;

    @media (max-width: 1023px) {
      padding-top: 0;
    }
  }

  .title {
    margin-bottom: 15px;
  }

  .caption {
    max-width: 460px;
    margin: 0 auto;
  }
</style>